/**
 * Settings Hooks
 *
 * React Query hooks for managing user settings and API keys
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { settingsApi } from '@/lib/api/settings';
import type { ChangePasswordInput } from '@/lib/api/settings';
import type {
  UpdateUserPreferencesRequest,
  ApiKeyCreateRequest,
} from '@/features/settings/types/settings.types';

/**
 * Query keys for settings
 */
export const settingsKeys = {
  all: ['settings'] as const,
  user: () => [...settingsKeys.all, 'user'] as const,
  apiKeys: () => [...settingsKeys.all, 'api-keys'] as const,
};

/**
 * Get user settings
 */
export const useUserSettings = () => {
  return useQuery({
    queryKey: settingsKeys.user(),
    queryFn: settingsApi.getUserSettings,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Update user preferences
 */
export const useUpdateUserPreferences = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateUserPreferencesRequest) =>
      settingsApi.updateUserPreferences(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.user() });
    },
  });
};

/**
 * List user's API keys
 */
export const useApiKeys = () => {
  return useQuery({
    queryKey: settingsKeys.apiKeys(),
    queryFn: settingsApi.listApiKeys,
    staleTime: 30 * 1000, // 30 seconds
  });
};

/**
 * Create new API key
 */
export const useCreateApiKey = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ApiKeyCreateRequest) => settingsApi.createApiKey(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.apiKeys() });
    },
  });
};

/**
 * Revoke API key
 */
export const useRevokeApiKey = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (keyId: string) => settingsApi.revokeApiKey(keyId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.apiKeys() });
    },
  });
};

/**
 * Change the logged-in user's password
 */
export const useChangePassword = () => {
  return useMutation({
    mutationFn: (data: ChangePasswordInput) => settingsApi.changePassword(data),
  });
};

/**
 * Regenerate API key
 */
export const useRegenerateApiKey = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (keyId: string) => settingsApi.regenerateApiKey(keyId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.apiKeys() });
    },
  });
};
